<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-primary fs-22">fact_check</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark">Protokol Terpadu Uji Similaritas & Deteksi AI (SOP RBN/SOP-RC/PAI/001)</h5>
      </div>
      <span v-if="protocol" class="badge bg-success-subtle text-success fs-12 px-3 py-2">
        {{ protocol.protocol_number }}
      </span>
      <span v-else class="badge bg-warning-subtle text-warning fs-12 px-3 py-2">
        Belum Dibuat
      </span>
    </div>

    <div class="card-body p-4">
      <!-- Existing Protocol Overview -->
      <div v-if="protocol" class="mb-4 p-3 rounded-3 bg-light border border-light-subtle">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div class="text-muted fs-12">Nomor Protokol Resmi (SOP RBN/SOP-RC/PAI/001)</div>
            <h5 class="fw-bold text-primary mb-0">{{ protocol.protocol_number }}</h5>
          </div>
          <div class="d-flex gap-2">
            <button @click="generatePdf" :disabled="generatingPdf" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm"></span>
              <span class="material-symbols-outlined fs-16">picture_as_pdf</span>
              <span>{{ generatingPdf ? 'Membuat PDF...' : 'Regenerate PDF RC-PAI' }}</span>
            </button>
            <a v-if="order.generated_protocol_pdf || protocol.generated_pdf" :href="getFileUrl(protocol.generated_pdf || order.generated_protocol_pdf)" target="_blank" class="btn btn-sm btn-success d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">download</span>
              <span>Unduh Dokumen Resmi A4</span>
            </a>
            <a v-if="protocol.verification?.verification_code" :href="getVerificationUrl(protocol.verification.verification_code)" target="_blank" class="btn btn-sm btn-info text-white d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">verified</span>
              <span>Cek Verifikasi Publik</span>
            </a>
          </div>
        </div>

        <div class="row g-2 fs-13">
          <div class="col-md-3">
            <span class="text-muted">Similaritas:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.similarity_overall_before }}% &rarr; {{ protocol.similarity_overall_after }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Deteksi AI:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.ai_score_initial }}% &rarr; {{ protocol.ai_score_final }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Keputusan:</span>
            <span class="badge ms-1" :class="protocol.overall_decision === 'MEMENUHI' ? 'bg-success' : 'bg-warning'">
              {{ protocol.overall_decision }}
            </span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Sumber Tertinggi:</span>
            <span class="fw-semibold text-dark ms-1">{{ protocol.highest_source_match }}%</span>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 pt-2 border-top fs-12 text-muted">
          <div class="d-flex gap-3">
            <div>
              <span class="material-symbols-outlined fs-14 align-middle text-primary">format_list_numbered</span>
              <strong class="text-dark ms-1">{{ (protocol.primary_sources || protocol.primarySources || []).length }}</strong> Primary Sources
            </div>
            <div>
              <span class="material-symbols-outlined fs-14 align-middle text-warning">rule</span>
              <strong class="text-dark ms-1">{{ (protocol.manual_exclusions || protocol.manualExclusions || []).length }}</strong> Excluded
            </div>
            <div>
              <span class="material-symbols-outlined fs-14 align-middle text-info">view_agenda</span>
              <strong class="text-dark ms-1">{{ (protocol.section_reviews || protocol.sectionReviews || []).length }}</strong> Bagian AI Terpetakan
            </div>
          </div>
          <button type="button" class="btn btn-xs btn-outline-secondary fs-11 py-1 px-2 d-flex align-items-center gap-1" @click="showDetailedOverview = !showDetailedOverview">
            <span class="material-symbols-outlined fs-14">{{ showDetailedOverview ? 'expand_less' : 'expand_more' }}</span>
            <span>{{ showDetailedOverview ? 'Tutup Rincian' : 'Lihat Rincian Output SOP' }}</span>
          </button>
        </div>

        <!-- Detail Preview Tabel Output Template Resmi -->
        <div v-if="showDetailedOverview" class="mt-3 pt-3 border-top">
          <div class="p-2 mb-3 bg-white rounded border">
            <div class="fw-bold fs-12 text-secondary mb-2">Parameter Standar &amp; Pengaturan (SOP RC-PAI):</div>
            <div class="row g-2 fs-12 text-dark">
              <div class="col-md-3">
                <span class="text-muted">Repository:</span> <strong>{{ protocol.repository_setting || 'No repository' }}</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Batas Maks. Total:</span> <strong>{{ protocol.applied_max_overall }}%</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Batas Sumber Tunggal:</span> <strong>{{ protocol.applied_max_single_source }}%</strong>
              </div>
              <div class="col-md-3">
                <span class="text-muted">Ambang Batas AI:</span> <strong>{{ protocol.ai_threshold }}%</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Protocol Edit/Create Form -->
      <form @submit.prevent="saveProtocol">
        <!-- 1. IDENTITAS & KLASIFIKASI NASKAH -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center gap-2 py-2 px-3">
            <span class="material-symbols-outlined text-primary fs-18">badge</span>
            <span class="fw-bold fs-13 text-dark">1. Identitas Pemeriksaan &amp; Klasifikasi Dokumen</span>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Profil Acuan</label>
                <select class="form-select form-select-sm" v-model="form.profile_id" @change="onProfileChange">
                  <option :value="null">-- Custom / Bebas --</option>
                  <option v-for="p in profiles" :key="p.id" :value="p.id">
                    {{ p.service_classification }} ({{ p.profile_name }})
                  </option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Klasifikasi Layanan <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.service_classification" required>
                  <option value="JAD Dosen">JAD Dosen</option>
                  <option value="Artikel Jurnal">Artikel Jurnal</option>
                  <option value="Skripsi/Tesis/Disertasi">Skripsi/Tesis/Disertasi</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Laporan">Laporan</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Versi Naskah <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.manuscript_version" required>
                  <option value="Draft Awal">Draft Awal</option>
                  <option value="Revisi 1">Revisi 1</option>
                  <option value="Revisi 2">Revisi 2</option>
                  <option value="Naskah Final">Naskah Final</option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Aplikasi / Software Pemeriksa <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.tool_name" required />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Repository Setting <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.repository_setting" required>
                  <option value="No repository">No repository (Standard RayaCheck)</option>
                  <option value="Standard repository">Standard repository</option>
                </select>
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Report / Submission ID</label>
                <input type="text" class="form-control form-control-sm" v-model="form.report_submission_id" placeholder="cth: 2198472918" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Tanggal Pemeriksaan <span class="text-danger">*</span></label>
                <input type="date" class="form-control form-control-sm" v-model="form.report_date" required />
              </div>
              <div class="col-md-8 mb-2">
                <label class="form-label fs-12">File Hash (SHA-256) <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm font-monospace fs-11" v-model="form.file_hash_sha256" required />
              </div>
            </div>
          </div>
        </div>

        <!-- 2. PARAMETER & BATAS TOLERANSI SIMILARITAS -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">settings_suggest</span>
              <span class="fw-bold fs-13 text-dark">2. Parameter &amp; Batas Toleransi Similaritas</span>
            </div>
            <button type="button" @click="describeSources = !describeSources" class="btn btn-xs btn-outline-warning text-dark fs-12 py-1 px-2 d-inline-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-14">rule</span>
              <span>{{ describeSources ? 'Tutup Rincian Sumber' : 'Kelola Sumber & Exclude' }}</span>
            </button>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Maks. Total Similaritas (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.applied_max_overall" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Maks. Sumber Tunggal (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.applied_max_single_source" required />
              </div>
              <div class="col-md-3 mb-2 d-flex align-items-center pt-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="form.applied_exclude_biblio" id="paiExBib" />
                  <label class="form-check-label fs-12" for="paiExBib">Exclude Bibliography</label>
                </div>
              </div>
              <div class="col-md-3 mb-2 d-flex align-items-center pt-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="form.applied_exclude_quotes" id="paiExQuotes" />
                  <label class="form-check-label fs-12" for="paiExQuotes">Exclude Quotes</label>
                </div>
              </div>
            </div>

            <div class="row mt-2 pt-2 border-top g-2 align-items-center">
              <div class="col-md-6">
                <label class="form-label fs-12 mb-1">Filter Exclude Small Matches</label>
                <div class="input-group input-group-sm">
                  <select class="form-select form-select-sm" v-model="form.small_matches_mode">
                    <option value="none">Tidak / None</option>
                    <option value="words">Words (Jumlah Kata)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  <input v-if="form.small_matches_mode !== 'none'" type="number" min="1" class="form-control form-control-sm" v-model.number="form.small_matches_value" :placeholder="form.small_matches_mode === 'words' ? 'cth: 8' : 'cth: 1'" />
                  <span v-if="form.small_matches_mode !== 'none'" class="input-group-text fs-12">{{ form.small_matches_mode === 'words' ? 'kata' : '%' }}</span>
                </div>
              </div>
              <div class="col-md-6 d-flex justify-content-between align-items-center pt-3">
                <span class="fs-12 text-secondary">
                  Manual Source Exclusion: <strong class="text-dark">{{ form.manual_exclusions.length }} sumber dicatat</strong>
                </span>
                <span class="fs-12 text-secondary">
                  Sumber Primer: <strong class="text-dark">{{ form.primary_sources.length }} sumber</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Checkbox: Deskripsikan Sumber Primer & Manual Source Exclusion (Sama seperti PLAGIARISM) -->
        <div class="card border rounded-3 mb-4 bg-light-subtle shadow-sm">
          <div class="card-body p-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div class="form-check d-flex align-items-center m-0">
              <input 
                class="form-check-input mt-0" 
                type="checkbox" 
                id="describeSourcesCheck" 
                v-model="describeSources"
                style="cursor: pointer; width: 1.25em; height: 1.25em;"
              >
              <label class="form-check-label fw-bold text-dark fs-13 ms-2 mb-0" for="describeSourcesCheck" style="cursor: pointer;">
                Deskripsikan
              </label>
              <span class="text-muted fs-12 ms-2">
                (Centang untuk membuka form &amp; tabel rincian Manual Source Exclusion dan Primary Sources jika diperlukan)
              </span>
            </div>
            <div>
              <span v-if="describeSources" class="badge bg-success-subtle text-success fs-11 px-2 py-1 border border-success-subtle d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-13">visibility</span>
                <span>Form &amp; Tabel Terbuka ({{ form.manual_exclusions.length }} Exclude, {{ form.primary_sources.length }} Primary)</span>
              </span>
              <span v-else class="badge bg-secondary-subtle text-secondary fs-11 px-2 py-1 border border-secondary-subtle d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-13">visibility_off</span>
                <span>Default: Tertutup (Tidak Wajib)</span>
              </span>
            </div>
          </div>
        </div>

        <!-- RINCIAN SUMBER & EXCLUDE (HANYA MUNCUL JIKA DICENTANG DESKRIPSIKAN) -->
        <div v-if="describeSources">
          <!-- MANUAL SOURCE EXCLUSION (LOG EKSKLUSI MANUAL SUMBER) -->
          <div class="card border rounded-3 mb-4 shadow-sm">
            <div class="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
              <div class="d-flex align-items-center gap-2">
                <span class="material-symbols-outlined text-warning fs-18">rule</span>
                <div>
                  <span class="fw-bold fs-13 text-dark">Manual Source Exclusion (Log Eksklusi Manual Sumber)</span>
                  <span class="badge bg-warning-subtle text-dark fs-11 ms-2">{{ form.manual_exclusions.length }} Sumber Dieksklusi</span>
                </div>
              </div>
              <button type="button" @click="addManualExclusion" class="btn btn-xs btn-warning text-dark d-flex align-items-center gap-1 py-1 px-2 fs-12 fw-medium shadow-sm">
                <span class="material-symbols-outlined fs-14">add</span>
                <span>Tambah Manual Source Exclusion</span>
              </button>
            </div>
            <div class="card-body p-3">
              <p class="fs-12 text-muted mb-2">
                <span class="material-symbols-outlined fs-14 align-middle text-primary">info</span>
                Sesuai SOP, jika pemeriksa mengecualikan kecocokan tertentu (seperti publikasi/preprint milik penulis sendiri, repository institusi, atau template resmi), cantumkan rincian dan alasannya demi audit kepatuhan integritas akademik.
              </p>
              <div v-if="form.manual_exclusions.length === 0" class="text-center py-3 text-muted fs-12 bg-light-subtle rounded border border-dashed">
                <span class="material-symbols-outlined fs-24 d-block mb-1 text-secondary">verified_user</span>
                Tidak ada sumber yang dieksklusi secara manual (Pemeriksaan mengikuti pengaturan filter standar).
                <div class="mt-2">
                  <button type="button" @click="addManualExclusion" class="btn btn-xs btn-outline-warning text-dark fs-12 py-1 px-2">
                    <span class="material-symbols-outlined fs-14 align-middle">add</span>
                    Tambah Manual Source Exclusion
                  </button>
                </div>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-bordered align-middle mb-0 fs-12">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 27%;">Sumber / Dokumen / URL</th>
                      <th style="width: 14%;">Tipe Sumber</th>
                      <th style="width: 12%;" class="text-center">Match Sblm Exclude (%)</th>
                      <th style="width: 21%;">Alasan Justifikasi Akademik</th>
                      <th style="width: 11%;">Petugas</th>
                      <th style="width: 11%;">Waktu Exclude</th>
                      <th style="width: 4%;" class="text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(exc, idx) in form.manual_exclusions" :key="idx">
                      <td>
                        <input type="text" class="form-control form-control-sm" v-model="exc.source_title_url" placeholder="cth: Preprint Penulis di ResearchGate" />
                      </td>
                      <td>
                        <select class="form-select form-select-sm" v-model="exc.source_type">
                          <option value="Self-match">Self-match</option>
                          <option value="Bibliografi">Bibliografi</option>
                          <option value="Quote">Quote</option>
                          <option value="Lainnya">Lainnya</option>
                        </select>
                      </td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="exc.match_before" />
                          <span class="input-group-text px-1">%</span>
                        </div>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm" v-model="exc.exclusion_reason" placeholder="cth: Publikasi skripsi penulis sendiri yang sudah terbit" />
                      </td>
                      <td>
                        <select class="form-select form-select-sm" v-model="exc.authorized_by">
                          <option value="Pemeriksa">Pemeriksa</option>
                          <option value="QC">QC</option>
                        </select>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm text-center fs-11" v-model="exc.excluded_at" placeholder="YYYY-MM-DD HH:mm:ss" />
                      </td>
                      <td class="text-center">
                        <button type="button" @click="removeManualExclusion(idx)" class="btn btn-sm btn-outline-danger p-1 border-0" title="Hapus Manual Exclusion">
                          <span class="material-symbols-outlined fs-16">delete</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- DAFTAR SUMBER SIMILARITAS UTAMA (PRIMARY SOURCES) -->
          <div class="card border rounded-3 mb-4 shadow-sm">
            <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
              <div class="d-flex align-items-center gap-2">
                <span class="material-symbols-outlined text-primary fs-18">list_alt</span>
                <span class="fw-bold fs-13 text-dark">Daftar Sumber Similaritas Utama (Primary Sources)</span>
                <span class="badge bg-primary-subtle text-primary fs-11">{{ form.primary_sources.length }} Sumber</span>
              </div>
              <button type="button" @click="addPrimarySource" class="btn btn-xs btn-primary d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-14">add</span>
                <span>Tambah Sumber</span>
              </button>
            </div>
            <div class="card-body p-3">
              <div v-if="form.primary_sources.length === 0" class="text-center py-3 text-muted fs-12 bg-light-subtle rounded border border-dashed">
                <span class="material-symbols-outlined fs-24 d-block mb-1 text-secondary">menu_book</span>
                Belum ada sumber primer yang dicatat. Klik <strong>"Tambah Sumber"</strong> untuk mencatat sumber kecocokan utama.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm align-middle fs-12 mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 5%;" class="text-center">#</th>
                      <th style="width: 45%;">Nama / Domain Sumber</th>
                      <th style="width: 15%;" class="text-center">Match (%)</th>
                      <th style="width: 15%;">Status</th>
                      <th style="width: 15%;">Catatan</th>
                      <th style="width: 5%;" class="text-center">Hapus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(src, idx) in form.primary_sources" :key="idx">
                      <td class="text-center fw-bold">{{ src.source_rank }}</td>
                      <td>
                        <input type="text" class="form-control form-control-sm" v-model="src.source_name" placeholder="cth: repository.ugm.ac.id" />
                      </td>
                      <td>
                        <div class="input-group input-group-sm">
                          <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="src.match_percentage" @input="recalcSources" />
                          <span class="input-group-text px-1">%</span>
                        </div>
                      </td>
                      <td>
                        <select class="form-select form-select-sm" v-model="src.status">
                          <option value="Wajar">Wajar</option>
                          <option value="Perlu revisi">Perlu revisi</option>
                          <option value="Klarifikasi">Klarifikasi</option>
                        </select>
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm" v-model="src.examiner_notes" placeholder="Catatan..." />
                      </td>
                      <td class="text-center">
                        <button type="button" @click="removePrimarySource(idx)" class="btn btn-sm btn-outline-danger p-1 border-0">
                          <span class="material-symbols-outlined fs-16">delete</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. HASIL EVALUASI SIMILARITAS -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">analytics</span>
              <span class="fw-bold fs-13 text-dark">3. Hasil Evaluasi Similaritas Teks</span>
            </div>
            <span class="badge bg-primary-subtle text-primary fs-11 d-inline-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-13">auto_awesome</span>
              <span>{{ describeSources && form.primary_sources.length > 0 ? 'Auto-Kalkulasi dari Sumber Primer' : 'Input Manual / Bebas' }}</span>
            </span>
          </div>
          <div class="card-body p-3">
            <div class="row g-2">
              <div class="col-md-3">
                <label class="form-label fs-12">Similaritas Awal (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.similarity_overall_before" required />
              </div>
              <div class="col-md-3">
                <label class="form-label fs-12 d-flex justify-content-between">
                  <span>Similaritas Akhir (%) <span class="text-danger">*</span></span>
                  <span class="text-muted fs-10" v-if="describeSources && form.primary_sources.length > 0">(Total Sumber)</span>
                </label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.similarity_overall_after" required />
              </div>
              <div class="col-md-3">
                <label class="form-label fs-12 d-flex justify-content-between">
                  <span>Sumber Tertinggi (%) <span class="text-danger">*</span></span>
                  <span class="text-muted fs-10" v-if="describeSources && form.primary_sources.length > 0">(Maks Sumber)</span>
                </label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.highest_source_match" required />
              </div>
              <div class="col-md-3">
                <label class="form-label fs-12">Nama Sumber Tertinggi</label>
                <input type="text" class="form-control form-control-sm" v-model="form.highest_source_name" placeholder="cth: repository.ugm.ac.id" />
              </div>
              <div class="col-md-6 mt-2">
                <label class="form-label fs-12">Status Uji Similaritas <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.similarity_result_status" required>
                  <option value="Memenuhi">Memenuhi</option>
                  <option value="Perlu revisi">Perlu revisi</option>
                  <option value="Perlu klarifikasi">Perlu klarifikasi</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. METRIK & PARAMETER DETEKSI AI -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center gap-2 py-2 px-3">
            <span class="material-symbols-outlined text-primary fs-18">smart_toy</span>
            <span class="fw-bold fs-13 text-dark">4. Metrik &amp; Parameter Deteksi AI</span>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Nama Detektor AI <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.ai_detector_name" required />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Versi Engine AI</label>
                <input type="text" class="form-control form-control-sm" v-model="form.ai_detector_version" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Ambang Batas AI Toleransi (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.ai_threshold" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Skor AI Awal (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.ai_score_initial" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Skor AI Akhir (%) <span class="text-danger">*</span></label>
                <input type="number" step="0.1" class="form-control form-control-sm" v-model.number="form.ai_score_final" @input="recalcAiDecision" required />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Zona Risiko AI <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.ai_internal_zone" required>
                  <option value="Zona Hijau / Aman">Zona Hijau / Aman</option>
                  <option value="Zona Perhatian">Zona Perhatian</option>
                  <option value="Zona Kritis">Zona Kritis</option>
                </select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label fs-12">Status Kode AI <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.ai_status_code" required>
                  <option value="PASS">PASS (Lolos)</option>
                  <option value="REVISE">REVISE (Perlu Revisi)</option>
                  <option value="NEED_REVIEW">NEED_REVIEW (Perlu Klarifikasi)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Checkbox: Deskripsikan Komposisi Konten & Deteksi AI Berbasis Blok (Drillbit Engine) -->
        <div class="card border rounded-3 mb-4 bg-light-subtle shadow-sm">
          <div class="card-body p-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div class="form-check d-flex align-items-center m-0">
              <input 
                class="form-check-input mt-0" 
                type="checkbox" 
                id="describeBlocksCheck" 
                v-model="describeBlocks"
                @change="onDescribeBlocksChange"
                style="cursor: pointer; width: 1.25em; height: 1.25em;"
              >
              <label class="form-check-label fw-bold text-dark fs-13 ms-2 mb-0" for="describeBlocksCheck" style="cursor: pointer;">
                Deskripsikan Komposisi Konten &amp; Rincian Blok Teks AI
              </label>
              <span class="text-muted fs-12 ms-2">
                (Centang jika ingin merinci diagram komposisi konten dan blok teks deteksi AI Drillbit. Opsional)
              </span>
            </div>
            <div>
              <span v-if="describeBlocks" class="badge bg-success-subtle text-success fs-11 px-2 py-1 border border-success-subtle d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-13">visibility</span>
                <span>Aktif ({{ form.section_reviews.length }} Blok Teks)</span>
              </span>
              <span v-else class="badge bg-secondary-subtle text-secondary fs-11 px-2 py-1 border border-secondary-subtle d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-13">visibility_off</span>
                <span>Default: Tidak Dijabarkan (Opsional)</span>
              </span>
            </div>
          </div>
        </div>

        <!-- 5. ANALISIS KOMPOSISI KONTEN & DETEKSI AI BERBASIS BLOK (DRILLBIT ENGINE) -->
        <div v-if="describeBlocks" class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center justify-content-between py-2 px-3">
            <div class="d-flex align-items-center gap-2">
              <span class="material-symbols-outlined text-primary fs-18">pie_chart</span>
              <span class="fw-bold fs-13 text-dark">5. Analisis Komposisi Konten &amp; Deteksi AI Berbasis Blok (Drillbit Engine)</span>
            </div>
            <div class="d-flex gap-2">
              <button type="button" @click="autoCalcComposition" class="btn btn-xs btn-outline-secondary d-flex align-items-center gap-1 fs-11">
                <span class="material-symbols-outlined fs-14">calculate</span>
                <span>Hitung Komposisi Otomatis</span>
              </button>
              <button type="button" @click="addSectionReview" class="btn btn-xs btn-primary d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-14">add</span>
                <span>Tambah Blok</span>
              </button>
            </div>
          </div>
          <div class="card-body p-3">
            <!-- Komposisi Konten Header Card -->
            <div class="p-3 bg-light-subtle rounded border mb-3">
              <div class="fs-12 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-16 text-primary">donut_large</span>
                <span>Komposisi Konten (Content Composition)</span>
              </div>
              <div class="row g-2">
                <div class="col-md-3">
                  <label class="form-label fs-12 mb-1">Human Written (%) <span class="text-danger">*</span></label>
                  <div class="input-group input-group-sm">
                    <input type="number" step="0.1" class="form-control form-control-sm text-center fw-bold text-success" v-model.number="form.ai_human_written_pct" required />
                    <span class="input-group-text">%</span>
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label fs-12 mb-1">AI Text Content (%) <span class="text-danger">*</span></label>
                  <div class="input-group input-group-sm">
                    <input type="number" step="0.1" class="form-control form-control-sm text-center fw-bold" :class="Number(form.ai_score_final) <= Number(form.ai_threshold) ? 'text-success' : 'text-danger'" v-model.number="form.ai_score_final" @input="recalcAiDecision" required />
                    <span class="input-group-text">%</span>
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label fs-12 mb-1">Excluded Content (%)</label>
                  <div class="input-group input-group-sm">
                    <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="form.ai_excluded_pct" placeholder="0" />
                    <span class="input-group-text">%</span>
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label fs-12 mb-1">Total Matched Words</label>
                  <div class="input-group input-group-sm">
                    <input type="number" class="form-control form-control-sm text-center" v-model.number="form.ai_matched_words" placeholder="cth: 1152" />
                    <span class="input-group-text">kata</span>
                  </div>
                </div>
              </div>
              <div class="text-muted fs-11 mt-1 fst-italic">
                * Komposisi di atas sesuai dengan diagram persentase Drillbit AI: Human Written + AI Text + Excluded = 100%.
              </div>
            </div>

            <!-- Block Level Analysis Table -->
            <div class="fs-12 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16 text-primary">segment</span>
              <span>AI Content Analysis — Block Level (Rincian Blok Teks)</span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm align-middle fs-12 mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 12%;" class="text-center">Blok #</th>
                    <th style="width: 14%;" class="text-center">Jumlah Kalimat</th>
                    <th style="width: 14%;" class="text-center">Jumlah Kata</th>
                    <th style="width: 16%;" class="text-center">AI Matched (%)</th>
                    <th style="width: 14%;" class="text-center">Status</th>
                    <th style="width: 25%;">Konteks Catatan</th>
                    <th style="width: 5%;" class="text-center">Hapus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(sec, idx) in form.section_reviews" :key="idx">
                    <td class="text-center fw-bold">
                      <div class="input-group input-group-sm justify-content-center">
                        <span class="input-group-text px-1 fs-11">Blok</span>
                        <input type="number" class="form-control form-control-sm text-center px-1" style="max-width: 50px;" v-model.number="sec.block_number" />
                      </div>
                    </td>
                    <td>
                      <input type="number" class="form-control form-control-sm text-center" v-model.number="sec.sentences_count" placeholder="cth: 4" />
                    </td>
                    <td>
                      <input type="number" class="form-control form-control-sm text-center" v-model.number="sec.words_count" placeholder="cth: 96" />
                    </td>
                    <td>
                      <div class="input-group input-group-sm">
                        <input type="number" step="0.1" class="form-control form-control-sm text-center" v-model.number="sec.ai_matched_pct" placeholder="cth: 100" />
                        <span class="input-group-text px-1">%</span>
                      </div>
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="sec.status">
                        <option value="INCLUDED">INCLUDED</option>
                        <option value="EXCLUDED">EXCLUDED</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="sec.context_note" placeholder="Konteks / rujukan..." />
                    </td>
                    <td class="text-center">
                      <button type="button" @click="removeSectionReview(idx)" class="btn btn-sm btn-outline-danger p-1 border-0">
                        <span class="material-symbols-outlined fs-16">delete</span>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="form.section_reviews.length === 0">
                    <td colspan="7" class="text-center text-muted py-3 bg-light-subtle rounded border border-dashed">
                      Belum ada rincian blok teks. Klik <strong>"Tambah Blok"</strong> jika ingin merinci blok-blok kalimat yang terdeteksi AI dari laporan Drillbit.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 6. KEPUTUSAN TERPADU, TIM PEMERIKSA & PENGESAHAN -->
        <div class="card border rounded-3 mb-4 shadow-sm">
          <div class="card-header bg-light d-flex align-items-center gap-2 py-2 px-3">
            <span class="material-symbols-outlined text-primary fs-18">gavel</span>
            <span class="fw-bold fs-13 text-dark">6. Keputusan Terpadu, Validasi &amp; Pengesahan Sertifikat</span>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label fs-12 fw-bold text-dark">Keputusan Menyeluruh <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" v-model="form.overall_decision" required>
                  <option value="MEMENUHI">MEMENUHI (Lolos Standar Mutu)</option>
                  <option value="REVISI DIANJURKAN">REVISI DIANJURKAN (Melebihi Ambang)</option>
                  <option value="KLARIFIKASI DIBUTUHKAN">KLARIFIKASI DIBUTUHKAN</option>
                  <option value="ESKALASI KE QC">ESKALASI KE QC</option>
                </select>
              </div>
              <div class="col-md-8 mb-3">
                <label class="form-label fs-12">Catatan Rekomendasi Terpadu</label>
                <input type="text" class="form-control form-control-sm" v-model="form.recommendation_notes" placeholder="cth: Dokumen memenuhi toleransi similaritas dan tingkat probabilitas AI dalam batas aman." />
              </div>

              <!-- Verifikasi 6 Parameter Integritas Akademik -->
              <div class="col-12 mb-3">
                <div class="p-3 bg-light rounded-2 border">
                  <div class="fw-bold fs-13 mb-2 text-dark d-flex align-items-center gap-1">
                    <span class="material-symbols-outlined fs-16 text-success">verified</span>
                    <span>Verifikasi 6 Parameter Integritas Akademik</span>
                  </div>
                  <div class="row g-2 fs-12 mb-2">
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_plagiarism" id="paiIntPlag">
                        <label class="form-check-label" for="paiIntPlag">Bebas Plagiarisme Substantif</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_fabrication" id="paiIntFab">
                        <label class="form-check-label" for="paiIntFab">Bebas Fabrikasi Data</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_falsification" id="paiIntFals">
                        <label class="form-check-label" for="paiIntFals">Bebas Falsifikasi Hasil</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_duplicate" id="paiIntDup">
                        <label class="form-check-label" for="paiIntDup">Bebas Redundan / Duplikasi</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_authorship" id="paiIntAuth">
                        <label class="form-check-label" for="paiIntAuth">Validitas Kepengarangan</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="form.integrity_coi" id="paiIntCoi">
                        <label class="form-check-label" for="paiIntCoi">Bebas Konflik Kepentingan</label>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <input type="text" class="form-control form-control-sm" v-model="form.integrity_notes" placeholder="Catatan Skrining Integritas Akademik (opsional, cth: Tidak ditemukan indikasi plagiasi substantif)" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Metadata Pengguna & Naskah -->
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Nama Pengguna / Penulis (Tampil di Protokol & PDF) <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.author_name_masked" required placeholder="Nama pengguna / penulis" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Judul Naskah / Dokumen (Tampil di Protokol & PDF) <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm" v-model="form.document_title_masked" required placeholder="Judul naskah / dokumen" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="form-label fs-12">Institusi / Afiliasi</label>
                <input type="text" class="form-control form-control-sm" v-model="form.institution" placeholder="Universitas ..." />
              </div>

              <!-- Tim Pemeriksa & Signatory Box -->
              <div class="col-12 mt-3">
                <div class="card bg-light-subtle border p-3 rounded-3">
                  <h6 class="fs-13 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                    <span class="material-symbols-outlined fs-18 text-primary">draw</span>
                    <span>Petugas Analis &amp; Penandatangan Pengesahan Protokol</span>
                  </h6>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label fs-12 fw-semibold">Nama Petugas Pemeriksa / Analis <span class="text-danger">*</span></label>
                      <input type="text" class="form-control form-control-sm" v-model="form.examiner_name" required />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label fs-12 fw-semibold">Petugas Quality Control (QC)</label>
                      <input type="text" class="form-control form-control-sm" v-model="form.qc_name" placeholder="Tim QC RBN" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label fs-12 fw-semibold">Pilih Penandatangan Terdaftar</label>
                      <select class="form-select form-select-sm" v-model="form.signatory_id" @change="onSignatoryChange">
                        <option :value="null">-- Gunakan Penandatangan Default --</option>
                        <option v-for="sig in signatories" :key="sig.id" :value="sig.id">
                          {{ sig.name }} ({{ sig.position }}) {{ sig.is_default ? '★ Default' : '' }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label fs-12 fw-semibold">Nama Penandatangan Dokumen</label>
                      <input type="text" class="form-control form-control-sm" v-model="form.signatory_name" placeholder="Nama Penandatangan" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label fs-12 fw-semibold">Jabatan Penandatangan</label>
                      <input type="text" class="form-control form-control-sm" v-model="form.signatory_position" placeholder="Direktur PT Raya Berdikari Nusantara" />
                    </div>
                    <div v-if="selectedSignatoryPreviewUrl" class="col-12 mt-1">
                      <div class="d-flex align-items-center gap-3 p-2 bg-white rounded border">
                        <span class="fs-11 text-muted fw-semibold">Preview Tanda Tangan:</span>
                        <img :src="selectedSignatoryPreviewUrl" alt="Tanda Tangan" style="height: 32px; max-width: 140px; object-fit: contain;" />
                        <span class="fs-11 text-success d-flex align-items-center gap-1">
                          <span class="material-symbols-outlined fs-14">check_circle</span>
                          <span>Siap disematkan pada sertifikat terpadu RC-PAI</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Disclaimer Checkbox -->
              <div class="col-12 mt-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="form.disclaimer_agreed" id="paiDiscCheck" required />
                  <label class="form-check-label fs-12 text-secondary" for="paiDiscCheck">
                    Saya menyetujui seluruh parameter pengujian dan memastikan data audit trail sesuai dengan hasil software resmi Drillbit Plagiarism &amp; AI.
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="d-flex justify-content-end gap-2 mt-4">
          <button type="submit" class="btn btn-primary px-4 py-2 d-flex align-items-center gap-2" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm"></span>
            <span class="material-symbols-outlined fs-18">save</span>
            <span>{{ protocol ? 'Perbarui Protokol RC-PAI' : 'Simpan & Terbitkan Protokol RC-PAI' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, computed, watch } from 'vue';
import api from '../../../services/api';
import protocolSignatoryService from '../../../services/protocolSignatoryService';

export default defineComponent({
  name: 'PlagiarismAiProtocolForm',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const submitting = ref(false);
    const generatingPdf = ref(false);
    const showDetailedOverview = ref(true);
    const describeSources = ref(false);
    const describeBlocks = ref(false);
    const profiles = ref<any[]>([]);
    const signatories = ref<any[]>([]);

    const protocol = computed(() => {
      return props.order.protocol_plagiarism_ai || props.order.protocolPlagiarismAi || null;
    });

    const form = reactive({
      profile_id: null as number | null,
      service_classification: 'Artikel Jurnal',
      manuscript_version: 'Draft Awal',
      tool_name: 'Drillbit Plagiarism & AI',
      examiner_account: '',
      repository_setting: 'No repository',
      report_submission_id: '',
      report_date: new Date().toISOString().substring(0, 10),
      file_hash_sha256: '',

      // Similaritas
      applied_max_overall: 20,
      applied_max_single_source: 5,
      applied_exclude_biblio: true,
      applied_exclude_quotes: true,
      applied_exclude_match: false,
      small_matches_mode: 'none' as string,
      small_matches_value: null as number | null,
      similarity_overall_before: 15,
      similarity_overall_after: 12,
      highest_source_match: 3,
      highest_source_name: '',
      similarity_result_status: 'Memenuhi',

      // AI Detektor
      ai_detector_name: 'Drillbit AI',
      ai_detector_version: 'v2.4',
      ai_threshold: 20,
      ai_score_initial: 10,
      ai_score_final: 5,
      ai_human_written_pct: 95 as number | null,
      ai_matched_words: null as number | null,
      ai_excluded_pct: 0 as number | null,
      ai_internal_zone: 'Zona Hijau / Aman',
      ai_status_code: 'PASS',

      // Keputusan Terpadu & Tim
      overall_decision: 'MEMENUHI',
      recommendation_notes: 'Dokumen memenuhi standar integritas karya ilmiah untuk similaritas teks dan batas probabilitas deteksi kecerdasan buatan.',
      integrity_plagiarism: true,
      integrity_fabrication: true,
      integrity_falsification: true,
      integrity_duplicate: true,
      integrity_authorship: true,
      integrity_coi: true,
      integrity_notes: '',
      examiner_name: 'Pemeriksa RBN',
      qc_name: 'Tim QC RBN',
      service_manager_name: 'Quality Assurance Head',
      director_name: 'Yulfitri, SKM., MPH',
      signatory_id: null as number | null,
      signatory_name: 'Yulfitri, SKM., MPH',
      signatory_position: 'Direktur PT Raya Berdikari Nusantara',

      // Verification metadata
      author_name_masked: '',
      document_title_masked: '',
      institution: '',
      disclaimer_agreed: false,

      // Children
      primary_sources: [] as any[],
      manual_exclusions: [] as any[],
      section_reviews: [] as any[]
    });

    const loadProfiles = async () => {
      try {
        const res = await api.get('/similarity-profiles');
        profiles.value = res.data.data || [];
      } catch (err) {
        console.error('Failed to load profiles:', err);
      }
    };

    const loadSignatories = async () => {
      try {
        const res = await protocolSignatoryService.getActive();
        signatories.value = res.data.data || [];
      } catch (err) {
        console.error('Failed to load signatories:', err);
      }
    };

    const selectedSignatoryPreviewUrl = computed(() => {
      if (!form.signatory_id) {
        const def = signatories.value.find(s => s.is_default);
        return def?.signature_image_url || null;
      }
      const selected = signatories.value.find(s => s.id === form.signatory_id);
      return selected?.signature_image_url || null;
    });

    const onSignatoryChange = () => {
      if (!form.signatory_id) return;
      const selected = signatories.value.find(s => s.id === form.signatory_id);
      if (selected) {
        form.signatory_name = selected.name;
        form.signatory_position = selected.position;
        form.director_name = selected.name;
      }
    };

    const onProfileChange = () => {
      if (!form.profile_id) return;
      const matched = profiles.value.find(p => p.id === form.profile_id);
      if (matched) {
        form.service_classification = matched.service_classification || form.service_classification;
        form.applied_max_overall = Number(matched.default_max_overall) || 20;
        form.applied_max_single_source = Number(matched.default_max_single_source) || 5;
        form.applied_exclude_biblio = Boolean(matched.exclude_bibliography);
        form.applied_exclude_quotes = Boolean(matched.exclude_quotes);
      }
    };

    const addPrimarySource = () => {
      form.primary_sources.push({
        source_rank: form.primary_sources.length + 1,
        source_name: '',
        match_percentage: 1.0,
        status: 'Wajar',
        examiner_notes: ''
      });
      recalcSources();
    };

    const removePrimarySource = (index: number) => {
      form.primary_sources.splice(index, 1);
      form.primary_sources.forEach((s, idx) => {
        s.source_rank = idx + 1;
      });
      recalcSources();
    };

    const addManualExclusion = () => {
      form.manual_exclusions.push({
        source_title_url: '',
        source_type: 'Self-match',
        match_before: 5.0,
        exclusion_reason: 'Self-citation karya atau repositori penulis sendiri yang diverifikasi',
        authorized_by: 'Pemeriksa',
        excluded_at: new Date().toISOString().substring(0, 19).replace('T', ' ')
      });
    };

    const removeManualExclusion = (index: number) => {
      form.manual_exclusions.splice(index, 1);
    };

    const recalcSources = () => {
      if (!describeSources.value || form.primary_sources.length === 0) return;
      let total = 0;
      let maxMatch = 0;
      let topName = '';
      for (const s of form.primary_sources) {
        const v = Number(s.match_percentage) || 0;
        total += v;
        if (v > maxMatch) {
          maxMatch = v;
          topName = s.source_name;
        }
      }
      form.similarity_overall_after = Number(total.toFixed(1));
      form.highest_source_match = Number(maxMatch.toFixed(1));
      if (topName) {
        form.highest_source_name = topName;
      }

      // Check status
      if (form.similarity_overall_after <= form.applied_max_overall && form.highest_source_match <= form.applied_max_single_source) {
        form.similarity_result_status = 'Memenuhi';
      } else {
        form.similarity_result_status = 'Perlu revisi';
      }
      recalcAiDecision();
    };

    const autoCalcComposition = () => {
      const aiScore = Number(form.ai_score_final) || 0;
      const exc = Number(form.ai_excluded_pct) || 0;
      form.ai_human_written_pct = Math.max(0, Number((100 - aiScore - exc).toFixed(1)));

      const wordsSum = form.section_reviews.reduce((sum: number, b: any) => {
        return (b.status === 'INCLUDED' && b.words_count) ? sum + Number(b.words_count) : sum;
      }, 0);
      if (wordsSum > 0) {
        form.ai_matched_words = wordsSum;
      }
    };

    const addSectionReview = () => {
      const nextNum = form.section_reviews.length + 1;
      form.section_reviews.push({
        block_number: nextNum,
        sentences_count: null,
        words_count: null,
        ai_matched_pct: 100,
        status: 'INCLUDED',
        section_name: `Blok ${nextNum}`,
        page_range: '',
        indicator_score: '100%',
        context_note: '',
        action: 'INCLUDED'
      });
    };

    const removeSectionReview = (index: number) => {
      form.section_reviews.splice(index, 1);
      form.section_reviews.forEach((b: any, idx: number) => {
        if (!b.block_number || b.block_number === idx + 2) {
          b.block_number = idx + 1;
        }
      });
    };

    const onDescribeBlocksChange = () => {
      if (describeBlocks.value) {
        if (form.ai_human_written_pct === null || form.ai_human_written_pct === undefined) {
          autoCalcComposition();
        }
      }
    };

    const recalcAiDecision = () => {
      const score = Number(form.ai_score_final) || 0;
      const thresh = Number(form.ai_threshold) || 20;

      if (describeBlocks.value && (form.ai_human_written_pct === null || form.ai_human_written_pct === undefined)) {
        autoCalcComposition();
      }

      if (score <= thresh) {
        form.ai_status_code = 'PASS';
        form.ai_internal_zone = 'Zona Hijau / Aman';
      } else if (score <= thresh + 10) {
        form.ai_status_code = 'NEED_REVIEW';
        form.ai_internal_zone = 'Zona Perhatian';
      } else {
        form.ai_status_code = 'REVISE';
        form.ai_internal_zone = 'Zona Kritis';
      }

      if (form.similarity_result_status === 'Memenuhi' && form.ai_status_code === 'PASS') {
        form.overall_decision = 'MEMENUHI';
      } else if (form.ai_status_code === 'REVISE' || form.similarity_result_status === 'Perlu revisi') {
        form.overall_decision = 'REVISI DIANJURKAN';
      } else {
        form.overall_decision = 'KLARIFIKASI DIBUTUHKAN';
      }
    };

    const syncWithOrder = () => {
      const ord = props.order;
      if (!ord) return;

      const pDetail = ord.plagiarism_ai_detail || ord.plagiarism_detail;
      if (pDetail) {
        form.applied_exclude_quotes = Boolean(pDetail.exclude_quotes);
        form.applied_exclude_biblio = Boolean(pDetail.exclude_bibliography);
        form.applied_exclude_match = Boolean(pDetail.exclude_match);
        if (pDetail.exclude_match) {
          form.small_matches_mode = 'words';
          form.small_matches_value = 8;
        }
        if (pDetail.source_file) {
          form.document_title_masked = pDetail.source_file.replace(/\.[^/.]+$/, '');
        }
      }

      if (ord.customer?.name && !form.author_name_masked) {
        form.author_name_masked = ord.customer.name;
      }
      if (!form.institution) {
        form.institution = ord.customer?.institution || ord.customer?.university || ord.customer?.agency || '';
      }
      if (!form.document_title_masked) {
        form.document_title_masked = ord.plagiarism_detail?.source_file || ord.paraphrase_detail?.source_file || ('Naskah Riset ' + (ord.order_code || ''));
      }
      if (ord.similarity_profile_id) {
        form.profile_id = ord.similarity_profile_id;
      }
      if (ord.service_classification) {
        form.service_classification = ord.service_classification;
      }

      // Generate random SHA-256 placeholder if not set
      if (!form.file_hash_sha256) {
        form.file_hash_sha256 = Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
      }

      // If protocol already exists, populate from protocol
      const existing = protocol.value;
      if (existing) {
        Object.assign(form, existing);
        if (existing.integrity_plagiarism !== undefined && existing.integrity_plagiarism !== null) {
          form.integrity_plagiarism = Boolean(existing.integrity_plagiarism);
          form.integrity_fabrication = Boolean(existing.integrity_fabrication);
          form.integrity_falsification = Boolean(existing.integrity_falsification);
          form.integrity_duplicate = Boolean(existing.integrity_duplicate);
          form.integrity_authorship = Boolean(existing.integrity_authorship);
          form.integrity_coi = Boolean(existing.integrity_coi);
          form.integrity_notes = existing.integrity_notes || '';
        }
        if (existing.small_matches_mode) {
          form.small_matches_mode = existing.small_matches_mode;
          form.small_matches_value = existing.small_matches_value !== null ? Number(existing.small_matches_value) : null;
        }
        if (existing.primary_sources || existing.primarySources) {
          form.primary_sources = JSON.parse(JSON.stringify(existing.primary_sources || existing.primarySources));
        } else {
          form.primary_sources = [];
        }
        if (existing.manual_exclusions || existing.manualExclusions) {
          form.manual_exclusions = JSON.parse(JSON.stringify(existing.manual_exclusions || existing.manualExclusions));
        } else {
          form.manual_exclusions = [];
        }
        if (existing.section_reviews || existing.sectionReviews) {
          form.section_reviews = JSON.parse(JSON.stringify(existing.section_reviews || existing.sectionReviews));
          form.section_reviews.forEach((sec: any, idx: number) => {
            if (!sec.block_number) sec.block_number = idx + 1;
            if (sec.ai_matched_pct === undefined && sec.indicator_score) {
              const num = parseFloat(sec.indicator_score);
              if (!isNaN(num)) sec.ai_matched_pct = num;
            }
            if (!sec.status) sec.status = sec.action || 'INCLUDED';
          });
        } else {
          form.section_reviews = [];
        }
        if (existing.ai_human_written_pct !== undefined && existing.ai_human_written_pct !== null) {
          form.ai_human_written_pct = Number(existing.ai_human_written_pct);
        } else if (existing.ai_score_final !== undefined) {
          form.ai_human_written_pct = Math.max(0, 100 - Number(existing.ai_score_final));
        }
        if (existing.ai_matched_words !== undefined && existing.ai_matched_words !== null) {
          form.ai_matched_words = Number(existing.ai_matched_words);
        }
        if (existing.ai_excluded_pct !== undefined && existing.ai_excluded_pct !== null) {
          form.ai_excluded_pct = Number(existing.ai_excluded_pct);
        }
        if (existing.verification) {
          form.author_name_masked = existing.verification.author_name_masked || form.author_name_masked;
          form.document_title_masked = existing.verification.document_title_masked || form.document_title_masked;
          form.institution = existing.verification.institution || form.institution;
        }
        describeSources.value = (form.primary_sources.length > 0 || form.manual_exclusions.length > 0);
        describeBlocks.value = Boolean(
          (form.section_reviews && form.section_reviews.length > 0) ||
          (existing && existing.ai_human_written_pct !== null && existing.ai_human_written_pct !== undefined)
        );
      } else {
        describeSources.value = false;
        describeBlocks.value = false;
        form.primary_sources = [];
        form.manual_exclusions = [];
      }
    };

    const saveProtocol = async () => {
      submitting.value = true;
      try {
        const payload = {
          ...form,
          author_name: form.author_name_masked,
          document_title: form.document_title_masked,
          primary_sources: describeSources.value ? form.primary_sources : [],
          manual_exclusions: describeSources.value ? form.manual_exclusions : [],
          section_reviews: describeBlocks.value ? form.section_reviews : [],
          ai_human_written_pct: describeBlocks.value ? form.ai_human_written_pct : null,
          ai_matched_words: describeBlocks.value ? form.ai_matched_words : null,
          ai_excluded_pct: describeBlocks.value ? form.ai_excluded_pct : null,
        };

        if (protocol.value) {
          await api.put(`/protocol-plagiarism-ai/${protocol.value.id}`, payload);
          alert('Protokol RC-PAI berhasil diperbarui.');
        } else {
          await api.post(`/orders/${props.order.order_id}/protocol-plagiarism-ai`, payload);
          alert('Protokol RC-PAI berhasil diterbitkan.');
        }
        emit('refresh');
      } catch (err: any) {
        console.error('Error saving RC-PAI:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan protokol RC-PAI.');
      } finally {
        submitting.value = false;
      }
    };

    const generatePdf = async () => {
      generatingPdf.value = true;
      try {
        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`, {
          protocol_type: 'RC-PAI'
        });
        alert('PDF Protokol RC-PAI berhasil dibuat ulang.');
        emit('refresh');
      } catch (err: any) {
        console.error('Error generating PDF:', err);
        alert(err.response?.data?.message || 'Gagal membuat PDF protokol.');
      } finally {
        generatingPdf.value = false;
      }
    };

    const getFileUrl = (path: string) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const base = (api.defaults.baseURL || '').replace('/api', '');
      return `${base}/storage/${path.replace(/^\/?storage\//, '')}`;
    };

    const getVerificationUrl = (code: string) => {
      return `${window.location.origin}/verify/${code}`;
    };

    onMounted(async () => {
      await Promise.all([loadProfiles(), loadSignatories()]);
      syncWithOrder();
    });

    watch(() => props.order, () => {
      syncWithOrder();
    }, { deep: true });

    return {
      protocol,
      form,
      profiles,
      signatories,
      submitting,
      generatingPdf,
      showDetailedOverview,
      describeSources,
      describeBlocks,
      selectedSignatoryPreviewUrl,
      onSignatoryChange,
      onProfileChange,
      onDescribeBlocksChange,
      addPrimarySource,
      removePrimarySource,
      addManualExclusion,
      removeManualExclusion,
      recalcSources,
      addSectionReview,
      removeSectionReview,
      autoCalcComposition,
      recalcAiDecision,
      saveProtocol,
      generatePdf,
      getFileUrl,
      getVerificationUrl
    };
  }
});
</script>

<style scoped>
.btn-xs {
  padding: 0.15rem 0.45rem;
  font-size: 0.75rem;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
